import classNames from 'classnames';
import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import SignaturePad from 'signature_pad';
import { useFocusFieldEvent } from '../../../contexts/events-context';
import { useTranslation } from '../../../sdk/components';
import { resizeCanvas } from '../utils/canvas';
import { dataUrlToWixFile } from '../utils/data-url';
import { decodePoints, encodePoints } from '../utils/points';
import styles from './signature-drawer.module.scss';
export const SignatureDrawer = (props)=>{
    const { className, onChange, value, theme, penColor, inputId, onBeforeChange, fieldId, onBlur, onFocus, defaultValue, hidden, readOnly } = props;
    const pad = useRef();
    const canvasRef = useRef();
    const resizeObserver = useRef(null);
    const valueUrl = value?.url;
    const { t } = useTranslation();
    const [ariaLabel, setAriaLabel] = useState(t('field.signature.canvas.aria-label.empty'));
    useFocusFieldEvent(()=>{
        canvasRef.current?.focus();
    }, fieldId);
    const options = useMemo(()=>({
            throttle: 0,
            dotSize: 2,
            minWidth: 1,
            maxWidth: 3,
            velocityFilterWeight: 0.2,
            penColor
        }), [
        penColor
    ]);
    const getSignature = useCallback(()=>{
        if (pad.current?.isEmpty()) {
            return '';
        }
        const dataPoints = pad.current?.toData();
        return dataPoints ? encodePoints(dataPoints) : pad.current?.toDataURL() ?? '';
    }, []);
    const handleChange = useCallback(()=>{
        const isEmpty = pad.current?.isEmpty();
        setAriaLabel(isEmpty ? t('field.signature.canvas.aria-label.empty') : t('field.signature.canvas.aria-label.signed'));
        onChange?.(dataUrlToWixFile(getSignature()));
    }, [
        getSignature,
        onChange,
        t
    ]);
    const handleBeforeChange = useCallback(()=>{
        onBeforeChange?.();
    }, [
        onBeforeChange
    ]);
    const mountCanvas = useCallback((node)=>{
        const isMount = node != null;
        if (isMount) {
            canvasRef.current = node;
            pad.current = new SignaturePad(node, options);
            resizeCanvas(canvasRef.current, pad.current);
            if (valueUrl) {
                const points = decodePoints(valueUrl);
                if (points) {
                    pad.current.fromData(points);
                    setAriaLabel(t('field.signature.canvas.aria-label.signed'));
                }
            } else {
                pad.current.clear();
                setAriaLabel(t('field.signature.canvas.aria-label.empty'));
            }
            pad.current.addEventListener('beginStroke', handleBeforeChange);
            pad.current.addEventListener('endStroke', handleChange);
            if (typeof ResizeObserver !== 'undefined' && !resizeObserver.current) {
                // SSR compatibility for ResizeObserver
                resizeObserver.current = new ResizeObserver(()=>{
                    if (canvasRef.current) {
                        resizeCanvas(canvasRef.current, pad?.current);
                    }
                });
            }
            resizeObserver.current?.observe(node);
        } else {
            // Cleanup on unmount
            resizeObserver.current?.disconnect();
            pad.current?.removeEventListener('endStroke', handleChange);
            pad.current?.removeEventListener('beginStroke', handleBeforeChange);
        }
    }, // onChange prop that comes from form root changes every render - it must be excluded from deps list
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
        options,
        t,
        valueUrl,
        handleBeforeChange
    ]);
    useEffect(()=>{
        if (!pad.current) {
            return;
        }
        if (readOnly) {
            pad.current.off();
        } else {
            pad.current.on();
        }
    }, [
        readOnly
    ]);
    useFocusFieldEvent(()=>{
        canvasRef.current?.focus();
    }, fieldId);
    return /*#__PURE__*/ React.createElement("div", {
        className: className,
        "data-theme": theme
    }, /*#__PURE__*/ React.createElement("canvas", {
        hidden: hidden,
        defaultValue: defaultValue,
        onFocus: onFocus,
        onBlur: onBlur,
        ref: mountCanvas,
        id: inputId,
        "data-input-id": inputId,
        "data-has-value": !!valueUrl,
        className: classNames(styles.canvas),
        "data-hook": "signature-canvas",
        role: "img",
        "aria-label": ariaLabel
    }));
};

//# sourceMappingURL=signature-drawer.js.map